import type {LineChartProps, BaseLineChartProps} from '../types'
import {MarkColor} from '../types'
import Chart from '../Chart'
import {
  getDataWithDefaults,
  getTransformedPlotOptions,
  getXAxisOptionWithDefaults,
  getYAxisOptionsWithDefaults,
  isDev,
} from './shared'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {CHART_CARD_CUSTOMIZATION_SETTINGS} from '../CustomizationDialog'

const MAX_NUMBER_OF_SERIES_ITEMS = 11

export const LineDefaultColor = {
  green: MarkColor.green,
  blue: MarkColor.blue,
  olive: MarkColor.olive,
  indigo: MarkColor.indigo,
  teal: MarkColor.teal,
  orange: MarkColor.orange,
  yellow: MarkColor.yellow,
  red: MarkColor.red,
  pink: MarkColor.pink,
  plum: MarkColor.plum,
  purple: MarkColor.purple,
}

export const BaseLineChart = <T extends 'line' | 'spline'>({
  type,
  options,
  series: data,
  marker,
  labels: initialLabelsValue,
  ...rest
}: BaseLineChartProps<T>) => {
  const {xAxis, yAxis, plot} = options ?? {}

  const [hasDashStyle] = useLocalStorage(CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardHasLineStyle, true)
  const [userLabelsValue] = useLocalStorage(CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardLineDataLabels, undefined)
  const labels = userLabelsValue ?? initialLabelsValue
  const xyAxisOptionsTransformed = getXAxisOptionWithDefaults({xAxis, gridLineDashStyle: 'Solid'})

  const yAxisOptionsTransformed = getYAxisOptionsWithDefaults({yAxis, gridLineDashStyle: 'Solid'})

  const seriesWithColors = data?.map((s, index) => {
    const keys = Object.keys(LineDefaultColor) as Array<keyof typeof LineDefaultColor>
    const colorKey = keys[index % keys.length]
    return {
      ...s,
      color: s.color ? `var(${MarkColor[s.color]})` : colorKey ? `var(${LineDefaultColor[colorKey]})` : undefined,
    }
  })

  const series = getDataWithDefaults({series: seriesWithColors as Highcharts.SeriesOptionsType[], type, hasDashStyle})

  const plotOptionsTransformed = getTransformedPlotOptions({
    plotOptions: plot,
    marker,
    labels,
  })

  if (series.length > MAX_NUMBER_OF_SERIES_ITEMS && isDev()) {
    // eslint-disable-next-line no-console
    console.error('Ideally, a line chart should contain no more than 11 lines.')
  }

  return (
    <Chart
      type={type}
      xAxisOptions={xyAxisOptionsTransformed}
      yAxisOptions={yAxisOptionsTransformed}
      series={series as Highcharts.SeriesOptionsType[]}
      plotOptions={plotOptionsTransformed}
      {...rest}
    />
  )
}

const LineChart = (props: LineChartProps) => {
  return <BaseLineChart {...props} type="line" />
}

export default LineChart

try{ BaseLineChart.displayName ||= 'BaseLineChart' } catch {}
try{ LineChart.displayName ||= 'LineChart' } catch {}